import { TFunction } from 'i18next';
import { FORM_SERVICE_TYPE, OPERATION_VERB, SERVICE_TYPE_KEY_VALUE } from '../../constants';

/**
 * @method getServiceEndPointLabel
 * @description Gets the label for service endpoint
 * @param serviceType service type value
 * @param translate i18 function
 * @returns Service endpoint label
 */
export const getServiceEndPointLabel = (serviceType: string, translate: TFunction<'translation', undefined, 'translation'>): string => {
  switch (serviceType) {
    case SERVICE_TYPE_KEY_VALUE.DB2:
      return translate(
        'service_details_form_component.lbl_jdbc_endpoint'
      );
    case SERVICE_TYPE_KEY_VALUE.MQ:
      return translate(
        'service_details_form_component.lbl_mq_endpoint'
      );
    default:
      return translate(
        'service_details_form_component.lbl_service_endpoint'
      );
  }
};

/**
 * @method getServiceEndPointPlaceholder
 * @description Gets the placeholder for service endpoint based on service type
 * @param serviceType service type value
 * @param translate i18 function
 * @returns Service endpoint placeholder
 */
export const getServiceEndPointPlaceholder = (serviceType: string, translate: TFunction<'translation', undefined, 'translation'>): string => {
  switch (serviceType) {
    case SERVICE_TYPE_KEY_VALUE.DB2:
      return translate(
        'service_details_form_component.plchd_service_endpoint_db2'
      );
    case SERVICE_TYPE_KEY_VALUE.MQ:
      return translate(
        'service_details_form_component.plchd_service_endpoint_mq'
      );
    default:
      return translate(
        'service_details_form_component.plchd_service_endpoint'
      );
  }
};

/**
 * @method getOperationVerbList
 * @description Gets the label for service operation verb based on service type
 * @param serviceType service type value
 * @param translate i18 function
 * @returns Operation verb label
 */
export const getOperationVerbList = (serviceType: string, translate: TFunction<'translation', undefined, 'translation'>) => {
  switch (serviceType.toLocaleLowerCase()) {
    case FORM_SERVICE_TYPE.DB2:
      return { type: OPERATION_VERB.slice(5, 8), translate: translate('service_operation_form.op_type_info_bubble') };
    case FORM_SERVICE_TYPE.MQ:
      return { type: OPERATION_VERB.slice(8, 11), translate: translate('service_operation_form.msg_type_info_bubble') };
    case FORM_SERVICE_TYPE.REST:
      return { type: OPERATION_VERB.slice(0, 5), translate: translate('service_operation_form.op_verb_info_bubble') };
    default:
      return { type: OPERATION_VERB.slice(0, 4), translate: translate('service_operation_form.op_verb_info_bubble') };
  }
};

/**
 * @method getOperationVerbLabel
 * @description Gets the label for service operation verb based on service type
 * @param serviceType service type value
 * @param translate i18 function
 * @returns Operation verb label
 */
export const getOperationVerbLabel = (serviceType: string, translate: TFunction<'translation', undefined, 'translation'>): string => {
  switch (serviceType.toLocaleLowerCase()) {
    case FORM_SERVICE_TYPE.DB2:
      return translate(
        'service_operation_list.lbl_service_operation_verb_db2'
      );
    case FORM_SERVICE_TYPE.MQ:
      return translate(
        'service_operation_list.lbl_service_operation_verb_mq'
      );
    default:
      return translate(
        'service_operation_list.lbl_service_operation_verb'
      );
  }
};

/**
 * @method getOperationUrlLabel
 * @description Gets the label for service operation url based on service type
 * @param serviceType service type value
 * @param translate i18 function
 * @returns Operation url label
 */
export const getOperationUrlLabel = (serviceType: string, translate: TFunction<'translation', undefined, 'translation'>): string => {
  switch (serviceType.toLocaleLowerCase()) {
    case FORM_SERVICE_TYPE.DB2:
      return translate(
        'service_operation_form.service_operation_url_db2'
      );
    case FORM_SERVICE_TYPE.MQ:
      return translate(
        'service_operation_form.service_operation_url_mq'
      );
    default:
      return translate(
        'service_operation_form.service_operation_url'
      );
  }
};

/**
 * @method getOperationUrlLabelForServiceOps
 * @description Returns the operation URL label for service operations.
 * @param serviceType - The type of service.
 * @param operationVerb - The operation verb.
 * @param translate - The translation function.
 * @returns The operation URL label.
 */
export const getOperationUrlLabelForServiceOps = (serviceType: string, operationVerb: string, translate: TFunction<'translation', undefined, 'translation'>): string => {
  let label = getOperationUrlLabel(serviceType, translate);
  if (operationVerb === OPERATION_VERB[5].name) {
    label = translate(
      'service_operation_form.service_operation_url_db2'
    );
  } else if (operationVerb === OPERATION_VERB[7].name) {
    label = translate(
      'service_operation_form.service_operation_url_procedure'
    );
  }
  return label;
};

/**
 * @method getOperationUrlPlaceholder
 * @description Gets the placeholder for service operation url based on service type
 * @param serviceType service type value
 * @param translate i18 function
 * @returns Operation url placeholder
 */
const getOperationUrlPlaceholder = (serviceType: string, translate: TFunction<'translation', undefined, 'translation'>): string => {
  switch (serviceType.toLocaleLowerCase()) {
    case FORM_SERVICE_TYPE.DB2:
      return translate(
        'service_operation_form.plchd_service_operation_url_db2'
      );
    case FORM_SERVICE_TYPE.MQ:
      return translate(
        'service_operation_form.plchd_service_operation_url_mq'
      );
    default:
      return translate(
        'service_operation_form.plchd_service_operation_url'
      );
  }
};

/**
 * @method getOperationUrlPlaceholder
 * @description Gets the placeholder for service operation url based on service type
 * @param serviceType service type value
 * @param translate i18 function
 * @returns Operation url placeholder
 */
export const getOperationUrlPlaceholderForDB2 = (serviceType: string, operationVerb: string, translate: TFunction<'translation', undefined, 'translation'>): string => {
  let label = getOperationUrlPlaceholder(serviceType, translate);

  if (operationVerb === OPERATION_VERB[5].name) {
    label = translate('service_operation_form.plchd_service_operation_url_query');
  } else if (operationVerb === OPERATION_VERB[6].name) {
    label = translate('service_operation_form.plchd_service_operation_url_query_insert');
  }
  return label;
};
